/**
 * Remembering whether this device is at a piano or on a train.
 *
 * Mode is a **device** preference, not session state. It is not in `SessionState` because
 * it does not describe the music: it describes the room. Restoring a session saved at the
 * piano onto a phone on a train must not switch that phone back into listening mode, and
 * a snapshot that carried the mode would do exactly that.
 *
 * It rides in the existing `preferences` object store under its own key. Out-of-line keys
 * mean a new record needs no `createObjectStore`, so there is **no `DB_VERSION` bump** —
 * an upgrade that has already run on somebody's iPad cannot be changed, and adding a key
 * to an existing store is precisely the kind of change that should never need one.
 *
 * `db.ts`'s header asks that only `queries.ts` know a store name. This module is the one
 * exception and it is a temporary one: it landed while `queries.ts` was owned by another
 * chunk. Fold `loadSessionMode`/`saveSessionMode` in there when convenient — the key and
 * the record shape below are the whole contract.
 */

import { getDb } from '../sketchbook/db.js';
import type { SessionMode } from '../components/system/sessionView.js';

/** The key the mode record lives under in the `preferences` store. */
export const SESSION_MODE_KEY = 'sessionMode';

/** What the app opens as before anyone has chosen. The piano is the product's home. */
export const DEFAULT_SESSION_MODE: SessionMode = 'piano';

/** Wrapped rather than stored bare, so the record can grow a field without a migration. */
interface SessionModeRecord {
  mode: SessionMode;
}

function isSessionMode(value: unknown): value is SessionMode {
  return value === 'piano' || value === 'on-the-go';
}

/**
 * The stored mode, or `null` when there is none — which is not the same as `'piano'`.
 * A caller that has never been told anything may want to *ask*; one that just wants a
 * mode can fall back to `DEFAULT_SESSION_MODE`.
 */
export async function loadSessionMode(): Promise<SessionMode | null> {
  const db = await getDb();
  const stored = await db.get<SessionModeRecord>('preferences', SESSION_MODE_KEY);
  return isSessionMode(stored?.mode) ? stored.mode : null;
}

export async function saveSessionMode(mode: SessionMode): Promise<void> {
  const db = await getDb();
  await db.put<SessionModeRecord>('preferences', { mode }, SESSION_MODE_KEY);
}
